import { createApiClient, isApiError } from '@rconf/api-client';

const DEFAULT_PATHS = Object.freeze({
  login: '/api/auth/login',
  refresh: '/api/auth/refresh',
  logout: '/api/auth/logout',
  nativeSession: '/api/auth/native/session',
  nativeRefresh: '/api/auth/native/refresh',
  nativeLogout: '/api/auth/native/logout',
});

export function createAuthSession(options = {}) {
  const adapter = options.adapter;
  if (!adapter || typeof adapter.restore !== 'function' || typeof adapter.refresh !== 'function') {
    throw new TypeError('createAuthSession requires an auth adapter with restore and refresh methods');
  }

  let state = makeState('anonymous');
  let restorePromise = null;
  let refreshPromise = null;
  let accessTokenRefreshAt = 0;
  const listeners = new Set();

  function publish(nextState) {
    state = Object.freeze(nextState);
    for (const listener of listeners) listener(state);
    return state;
  }

  function authenticate(payload) {
    const accessToken = accessTokenFromPayload(payload);
    if (!accessToken) throw new TypeError('Authentication response did not include an access token');
    accessTokenRefreshAt = refreshAtFromPayload(payload, options);
    return publish(makeState('authenticated', {
      accessToken,
      user: payload?.user ?? null,
      impersonation: payload?.impersonation ?? null,
    }));
  }

  function clear(error = null) {
    accessTokenRefreshAt = 0;
    return publish(makeState('anonymous', { error }));
  }

  async function restore() {
    if (restorePromise) return restorePromise;
    publish(makeState('restoring', {
      accessToken: state.accessToken,
      user: state.user,
      impersonation: state.impersonation,
    }));
    restorePromise = Promise.resolve()
      .then(() => adapter.restore())
      .then((payload) => (payload ? authenticate(payload) : clear()))
      .catch((error) => clear(error))
      .finally(() => {
        restorePromise = null;
      });
    return restorePromise;
  }

  async function login(credentials, loginOptions = {}) {
    if (typeof adapter.login !== 'function') {
      throw new TypeError('This auth adapter does not support interactive login');
    }
    publish(makeState('authenticating', {
      accessToken: state.accessToken,
      user: state.user,
      impersonation: state.impersonation,
    }));
    try {
      return authenticate(await adapter.login(credentials, loginOptions));
    } catch (error) {
      clear(error);
      throw error;
    }
  }

  async function refresh(context = {}) {
    if (refreshPromise) return refreshPromise;
    refreshPromise = Promise.resolve()
      .then(() => adapter.refresh({
        ...context,
        accessToken: state.accessToken,
        user: state.user,
      }))
      .then((payload) => {
        authenticate(payload);
        return payload;
      })
      .catch((error) => {
        clear(error);
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
    return refreshPromise;
  }

  async function logout() {
    const currentState = state;
    try {
      if (typeof adapter.logout === 'function') {
        await adapter.logout({
          accessToken: currentState.accessToken,
          user: currentState.user,
        });
      }
    } finally {
      clear();
    }
  }

  function setAccessToken(accessToken) {
    const cleanToken = String(accessToken || '').trim();
    if (!cleanToken) return clear();
    if (cleanToken !== state.accessToken) {
      accessTokenRefreshAt = refreshAtFromPayload({}, options);
    }
    return publish(makeState('authenticated', {
      accessToken: cleanToken,
      user: state.user,
      impersonation: state.impersonation,
    }));
  }

  return Object.freeze({
    getState: () => state,
    getAccessToken: () => state.accessToken,
    setAccessToken,
    restore,
    login,
    refresh,
    logout,
    clear,
    subscribe(listener) {
      if (typeof listener !== 'function') throw new TypeError('Session listener must be a function');
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    apiClientOptions: () => ({
      getAccessToken: async () => {
        if (state.accessToken && accessTokenRefreshAt && Date.now() >= accessTokenRefreshAt) {
          await refresh({ reason: 'access-token-expiring' });
        }
        return state.accessToken;
      },
      setAccessToken: (token) => { setAccessToken(token); },
      refreshAccessToken: refresh,
      onAuthFailure: (error) => { clear(error); },
    }),
  });
}

function refreshAtFromPayload(payload, options) {
  const advertisedSeconds = Number(payload?.expires_in ?? payload?.access_expires_in ?? 0);
  const fallbackSeconds = Number(options.accessTokenTtlSeconds || 0);
  const lifetimeSeconds = advertisedSeconds > 0 ? advertisedSeconds : fallbackSeconds;
  if (!Number.isFinite(lifetimeSeconds) || lifetimeSeconds <= 0) return 0;
  const configuredSkew = Math.max(0, Number(options.accessTokenRefreshSkewSeconds ?? 60));
  const skewSeconds = Math.min(configuredSkew, lifetimeSeconds / 2);
  return Date.now() + Math.max(1, lifetimeSeconds - skewSeconds) * 1000;
}

export function createBrowserAuthAdapter(options = {}) {
  const client = authTransport(options);
  const paths = { ...DEFAULT_PATHS, ...(options.paths || {}) };

  async function restore() {
    try {
      return await refresh();
    } catch (error) {
      if (isUnauthorized(error)) return null;
      throw error;
    }
  }

  function refresh() {
    return client.post(paths.refresh, undefined, unauthenticated());
  }

  return Object.freeze({
    kind: 'browser-cookie',
    restore,
    refresh,
    login: (credentials, loginOptions = {}) => client.post(
      loginOptions.path || paths.login,
      credentials,
      unauthenticated(),
    ),
    logout: ({ accessToken } = {}) => client.post(
      paths.logout,
      undefined,
      unauthenticated(accessToken),
    ),
  });
}

export function createElectronAuthAdapter(options = {}) {
  const bridge = options.bridge;
  if (!bridge || typeof bridge.refreshAuthSession !== 'function') {
    throw new TypeError('Electron auth adapter requires bridge.refreshAuthSession');
  }

  return Object.freeze({
    kind: 'electron-cookie',
    restore: async () => {
      try {
        return await (typeof bridge.restoreAuthSession === 'function'
          ? bridge.restoreAuthSession()
          : bridge.refreshAuthSession({ reason: 'restore' }));
      } catch (error) {
        if (isUnauthorized(error)) return null;
        throw error;
      }
    },
    refresh: () => bridge.refreshAuthSession({ reason: 'refresh' }),
    login: typeof bridge.loginAuthSession === 'function'
      ? (credentials, loginOptions = {}) => bridge.loginAuthSession(credentials, loginOptions)
      : undefined,
    logout: typeof bridge.logoutAuthSession === 'function'
      ? (context) => bridge.logoutAuthSession(context)
      : undefined,
  });
}

export function createNativeAuthAdapter(options = {}) {
  const store = secureStoreAdapter(options.secureStore);
  const client = authTransport({ ...options, credentials: options.credentials || 'omit' });
  const paths = { ...DEFAULT_PATHS, ...(options.paths || {}) };
  const refreshTokenKey = String(options.refreshTokenKey || 'rconf.native.refresh-token');

  async function persist(payload) {
    const refreshToken = String(payload?.refresh_token || '').trim();
    if (!refreshToken) throw new TypeError('Native authentication response did not include a refresh token');
    await store.set(refreshTokenKey, refreshToken);
    return payload;
  }

  async function refreshWithStoredToken({ tolerateUnauthorized = false } = {}) {
    const refreshToken = await store.get(refreshTokenKey);
    if (!refreshToken) return null;
    try {
      return await persist(await client.post(
        paths.nativeRefresh,
        { refresh_token: refreshToken },
        unauthenticated(),
      ));
    } catch (error) {
      if (isUnauthorized(error)) {
        await store.remove(refreshTokenKey);
        if (tolerateUnauthorized) return null;
      }
      throw error;
    }
  }

  async function establish(authPayload) {
    const accessToken = accessTokenFromPayload(authPayload);
    if (!accessToken) throw new TypeError('Native session exchange requires an access token');
    return persist(await client.post(
      paths.nativeSession,
      undefined,
      unauthenticated(accessToken),
    ));
  }

  return Object.freeze({
    kind: 'native-secure-store',
    restore: () => refreshWithStoredToken({ tolerateUnauthorized: true }),
    refresh: () => refreshWithStoredToken(),
    establish,
    async login(credentials, loginOptions = {}) {
      const authPayload = await client.post(
        loginOptions.path || paths.login,
        credentials,
        unauthenticated(),
      );
      return establish(authPayload);
    },
    async logout({ accessToken } = {}) {
      const refreshToken = await store.get(refreshTokenKey);
      try {
        if (refreshToken) {
          await client.post(
            paths.nativeLogout,
            { refresh_token: refreshToken },
            unauthenticated(accessToken),
          );
        }
      } finally {
        await store.remove(refreshTokenKey);
      }
    },
  });
}

function authTransport(options) {
  if (options.client && typeof options.client.post === 'function') return options.client;
  return createApiClient({
    baseUrl: options.baseUrl || '',
    fetchImpl: options.fetchImpl,
    credentials: options.credentials || 'include',
  });
}

function secureStoreAdapter(store) {
  if (!store) throw new TypeError('Native auth adapter requires a secureStore implementation');
  const get = store.getItemAsync || store.getItem;
  const set = store.setItemAsync || store.setItem;
  const remove = store.deleteItemAsync || store.removeItem || store.deleteItem;
  if (typeof get !== 'function' || typeof set !== 'function' || typeof remove !== 'function') {
    throw new TypeError('secureStore must provide get, set and remove operations');
  }
  return {
    get: (key) => get.call(store, key),
    set: (key, value) => set.call(store, key, value),
    remove: (key) => remove.call(store, key),
  };
}

function unauthenticated(accessToken = '') {
  const token = String(accessToken || '').trim();
  return {
    auth: false,
    retryAuth: false,
    ...(token ? { headers: { authorization: `Bearer ${token}` } } : {}),
  };
}

function accessTokenFromPayload(payload) {
  if (!payload || typeof payload !== 'object') return '';
  return String(payload.access_token || payload.accessToken || payload.token || '').trim();
}

function isUnauthorized(error) {
  return isApiError(error) ? error.status === 401 : Number(error?.status || 0) === 401;
}

function makeState(status, values = {}) {
  return {
    status,
    accessToken: String(values.accessToken || ''),
    user: values.user ?? null,
    impersonation: values.impersonation ?? null,
    error: values.error ?? null,
  };
}
